import {
  type AutomatedOutreach as OutreachData,
  useCreateAutomatedOutreachMutation,
} from '@cartwheel/api/v1'
import { Button } from '@cartwheel/components'
import { clsx } from 'clsx'
import { useState } from 'react'

interface AutomatedOutreachStatusProps {
  outreach: OutreachData | null
  showStartButton: boolean
  referralId: string
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  started: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  closed: 'bg-gray-100 text-gray-700',
}

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  other: 'Other',
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function AutomatedOutreachStatus({
  outreach,
  showStartButton,
  referralId,
}: AutomatedOutreachStatusProps) {
  const [timelineOpen, setTimelineOpen] = useState(false)

  const mutation = useCreateAutomatedOutreachMutation({
    onSuccess: () => {
      window.location.reload()
    },
  })

  const handleStartOutreach = () => {
    mutation.mutate({ body: { referral_id: referralId } })
  }

  return (
    <div className="mt-4 rounded-md border border-gray-200 bg-white p-4">
      <p className="mb-2 text-sm font-semibold text-charcoal-80">Automated Outreach</p>

      {outreach ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={clsx(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                STATUS_BADGE_CLASSES[outreach.status] ?? 'bg-gray-100 text-gray-700'
              )}
            >
              {outreach.status_label}
            </span>
            <span className="text-sm text-charcoal-50">{outreach.duration}</span>
            <span className="text-sm text-charcoal-80">Stage: {outreach.current_stage}</span>
          </div>

          {outreach.communication_timeline.length > 0 && (
            <div className="mt-3">
              <Button
                variant="link"
                onClick={() => setTimelineOpen((open) => !open)}
                aria-expanded={timelineOpen}
              >
                {timelineOpen ? 'Hide' : 'Show'} communication timeline
                <span aria-hidden="true">{timelineOpen ? '▲' : '▼'}</span>
              </Button>

              {timelineOpen && (
                <div className="mt-2 space-y-3">
                  {outreach.communication_timeline.map((stageGroup) => (
                    <div key={stageGroup.stage}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-50">
                        {stageGroup.stage}
                      </p>
                      <ul className="mt-1 space-y-1">
                        {stageGroup.events.map((event) => (
                          <li
                            key={`${event.event_type}-${event.occurred_at}`}
                            className="flex items-start gap-2 text-sm text-charcoal-80"
                          >
                            <span className="mt-0.5 shrink-0 text-xs font-medium text-charcoal-50">
                              {CHANNEL_LABELS[event.channel] ?? event.channel}
                            </span>
                            <span className="flex-1">{event.note}</span>
                            <span className="shrink-0 text-xs text-charcoal-50">
                              {formatDate(event.occurred_at)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-charcoal-50">No automated outreach has been started.</p>
      )}

      {showStartButton && (
        <div className="mt-3">
          <Button variant="primary" onClick={handleStartOutreach} disabled={mutation.isPending}>
            {mutation.isPending ? 'Starting…' : 'Begin Automated Outreach'}
          </Button>
          {mutation.isError && (
            <p className="mt-1 text-xs text-red-600">Failed to start outreach. Please try again.</p>
          )}
        </div>
      )}
    </div>
  )
}
